import React, { useState, useEffect } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";

import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";

import { useSelector, useDispatch } from "react-redux";

import { removeItemWithSlice } from "util/utils";
import API from "util/API";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "store/actions/snackbarMessage";

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: theme.spacing(3),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  emptyIcon: {
    fontSize: 40,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1),
  },
  tableCard: {
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  tableHeadRow: {
    backgroundColor: theme.palette.grey[50],
  },
}));

const PaymentRequiredQRCodeTable = (props) => {
  const { className, ...rest } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [uiLoading, setUiLoading] = useState(false);
  const [tableNumber, setTableNumber] = useState("");
  const [tableNumbers, setTableNumbers] = useState([]);
  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  const showSuccess = (msg) => dispatch(showSuccessSnackbar(msg));
  const restaurant = useSelector(
    (state) => state.restaurant.selectedRestaurant
  );

  const save = () => {
    if (!restaurant) {
      showError("Please select a restaurant first.");
      return;
    }

    const trimmed = tableNumber.trim();
    if (trimmed === "") {
      showError("The table number cannot be empty");
      return;
    }

    if (tableNumbers) {
      const exists = tableNumbers.some(
        (tableNo) => tableNo.toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) {
        showError("The table number already exists.");
        return;
      }
    }

    const tableNos =
      tableNumbers.length > 0 ? [...tableNumbers, trimmed] : [trimmed];
    setTableNumbers(tableNos);
    updatePaymentTables(tableNos);
  };

  const updatePaymentTables = (tableNos) => {
    if (!restaurant) {
      showError("Please go back to the restaurant screen and try again.");
      return;
    }

    setUiLoading(true);
    API.put("/updateRestaurantValues/", {
      paymentRequiredTableNumbers: tableNos,
    })
      .then(() => {
        setUiLoading(false);
        showSuccess("Updated successfully");
        setTableNumber("");
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          props.history.push("/sign-in");
        }
        setUiLoading(false);
      });
  };

  const handleDelete = (tableNo) => {
    const deleteIndx = tableNumbers.findIndex((item) => item === tableNo);
    if (deleteIndx !== -1) {
      const numbers = removeItemWithSlice(tableNumbers, deleteIndx);
      setTableNumbers(numbers);
      updatePaymentTables(numbers);
      setTableNumber("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      save();
    }
  };

  useEffect(() => {
    if (restaurant && restaurant.paymentRequiredTableNumbers) {
      setTableNumbers(restaurant.paymentRequiredTableNumbers);
    }
  }, [restaurant]);

  const count = tableNumbers.length;

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardHeader
        title="Payment Required Tables"
        subheader="Tables listed here require guests to pay online before their order is sent to the kitchen."
        action={
          <Chip
            label={`${count} ${count === 1 ? "table" : "tables"}`}
            size="small"
            color={count > 0 ? "primary" : "default"}
            variant={count > 0 ? "filled" : "outlined"}
          />
        }
      />
      <Divider />

      {uiLoading && <LinearProgress />}

      <CardContent className={classes.content}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item md={4} sm={6} xs={12}>
            <TextField
              fullWidth
              label="Table number"
              name="tableNumber"
              variant="outlined"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 12"
              helperText="Press Enter or click Add to save"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              disabled={uiLoading || tableNumber.trim() === ""}
              onClick={save}
              sx={{ mt: 0.5 }}
            >
              Add table
            </Button>
          </Grid>
        </Grid>

        <Box className={classes.tableCard}>
          {count === 0 ? (
            <Box className={classes.emptyState}>
              <TableRestaurantIcon className={classes.emptyIcon} />
              <Typography variant="subtitle1">
                No payment-required tables yet
              </Typography>
              <Typography variant="body2">
                Add a table number above to require prepayment before the order
                is accepted.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell>
                    <Typography variant="subtitle2">Table</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2">Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableNumbers.map((tableNo) => (
                  <TableRow hover key={tableNo}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <TableRestaurantIcon fontSize="small" color="action" />
                        <Typography variant="body2">{tableNo}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={`delete table ${tableNo}`}
                        edge="end"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(tableNo)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

PaymentRequiredQRCodeTable.propTypes = {
  className: PropTypes.string,
};

export default PaymentRequiredQRCodeTable;
